export type ExternalId = {
  type: string;
  display_name: string;
  value: string;
};

export type MedicalEntity = {
  name: string;
  external_ids?: ExternalId[];
  external_names?: string[];
  alternate_names?: string[];
  type: string;
};

export type FAQ = {
  question: string;
  answer: string;
};

export type SharedFaq = {
  faqs?: FAQ[];
  name: string;
  locale?: string;
};

export type Disclaimer = {
  name: string;
  disclaimer: string;
};

type Metadata = {
  meta?: {
    title?: string;
    description?: string;
  };
};

export type Care = Metadata & {
  medical_entities?: MedicalEntity[];
  name: string;
  appointment_reason: string;
  faqs?: FAQ[];
  description: string | null;
  brief_description?: string;
  external_ids?: ExternalId[];
  high_value_keywords?: string[];
  slug: string;
  locale?: string;
  external_names?: string[];
  rank_boost: number;
  id: string;
  related_to?: Care[];
  high_value_medical_entities?: MedicalEntity[];
  keywords?: string[];
  shared_faqs?: SharedFaq[];
  disclaimers?: Disclaimer[];
  alternate_names?: string[];
  type: string;
  insurance_type: CareInsuranceType;
  override_url?: string;
};

export type RawCareEntrySearchResult = {
  name: string;
  appointment_reason?: string;
  description?: string;
  slug: string;
  brief_description?: string;
  override_url?: string;
  cares?: RelatedCareOverrideWrapper[];
  related_topics?: RelatedTopicOverrideWrapper[];
};

export type CareEntrySearchResult = {
  name: string;
  appointment_reason?: string | null;
  description?: string | null;
  slug: string;
  brief_description?: string | null;
  override_url?: string | null;
  cares?: RelatedCare[] | null;
  related_topics?: RelatedTopic[];
};

export type Hit<T> = {
  score: number;
  document: T;
};

export type SearchResponse<T> = {
  hits: T[];
  limit: number;
  offset: number;
  status: string;
  total: number;
};

export type RelatedCare = Pick<
  Care,
  | "name"
  | "appointment_reason"
  | "description"
  | "slug"
  | "locale"
  | "rank_boost"
  | "type"
  | "brief_description"
>;

export type RelatedCareOverrideWrapper = {
  name?: string;
  description?: string;
  brief_description?: string;
  care: RelatedCare;
};

export type RelatedTopic = {
  brief_description?: string | null;
  description?: string | null;
  insurance_type: CareInsuranceType;
  locale: string;
  name: string;
  rank_boost: number;
  slug: string;
};

export type RelatedTopicOverrideWrapper = {
  brief_description: string | null;
  name: string | null;
  description: string | null;
  topic: RelatedTopic;
};

export enum CareInsuranceType {
  Standard = "Standard",
  Medicaid = "Medicaid",
  NoInsurance = "NoInsurance",
  OccupationalHealth = "OccupationalHealth",
}

export type CareTopicRaw = Metadata & {
  id: string;
  name: string;
  brief_description?: string;
  description: string;
  shared_faqs?: SharedFaq[];
  model_name: "topic";
  faqs?: FAQ[];
  slug: string;
  cares: RelatedCareOverrideWrapper[];
  insurance_type: CareInsuranceType;
  related_topics: RelatedTopicOverrideWrapper[];
};

export type CareTopic = Omit<CareTopicRaw, "cares" | "related_topics"> & {
  cares: RelatedCare[];
  related_topics: RelatedTopic[];
};
